import { useState, useEffect } from "react";
import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { Button } from "@/project/jain_elibrary-admin/components/ui/button";
import { Input } from "@/project/jain_elibrary-admin/components/ui/input";
import { Label } from "@/project/jain_elibrary-admin/components/ui/label";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/project/jain_elibrary-admin/components/ui/avatar";
import { getAdminUserInfo, setAdminUserInfo } from "@lib/storeManager";
import { adminService } from "@/project/jain_elibrary-admin/services/adminService";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { showErrorToast } from "@/utils/errorHandler";

const UserProfile = () => {
  const [userInfo, setUserInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setFetching(true);
        // Try fetching from API first for freshest data
        const profile = await adminService.getProfile();
        setUserInfo(profile);
        // Also update local storage if it differs
        setAdminUserInfo({
          ...getAdminUserInfo(),
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
        });
      } catch (error) {
        console.error("Failed to fetch profile:", error);
        // Fallback to local storage if API fails
        const data = getAdminUserInfo();
        if (data) {
          setUserInfo(data);
        }
      } finally {
        setFetching(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    // Map id to the property name used in userInfo
    const fieldMap: { [key: string]: string } = {
      firstName: "first_name",
      lastName: "last_name",
    };

    const field = fieldMap[id];
    if (field) {
      setUserInfo((prev: any) => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData = {
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
      };

      const updatedProfile = await adminService.updateProfile(updateData);

      // Update local state and storage
      setUserInfo(updatedProfile);
      const localData = getAdminUserInfo();
      setAdminUserInfo({
        ...localData,
        first_name: updatedProfile.first_name,
        last_name: updatedProfile.last_name,
      });

      // Notify other components (like Sidebar/Header)
      window.dispatchEvent(new Event("userInfoUpdated"));

      toast.success("Profile updated successfully");
    } catch (error: any) {
      showErrorToast(error, "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <AdminLayout>
        <div className="flex h-full items-center justify-center p-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="ml-2 text-muted-foreground">Loading profile...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!userInfo) {
    return (
      <AdminLayout>
        <div className="flex h-full items-center justify-center">
          <p>No profile information found.</p>
        </div>
      </AdminLayout>
    );
  }

  const initials =
    `${userInfo.first_name?.[0] ?? ""}${userInfo.last_name?.[0] ?? ""}`.toUpperCase() ??
    "AD";

  return (
    <AdminLayout>
      <div className="mx-auto max-w-2xl space-y-6">
        <h2 className="text-3xl font-bold text-foreground">User Profile</h2>

        <div className="rounded-lg border border-border bg-card p-8 shadow-sm">
          <div className="mb-8 flex items-center gap-6">
            <Avatar className="h-24 w-24 ring-2 ring-primary/10">
              <AvatarImage src="/placeholder.svg" />
              <AvatarFallback className="bg-primary/5 text-2xl text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-bold">
                {userInfo.first_name} {userInfo.last_name}
              </h3>
            </div>
          </div>

          <div className="space-y-4 text-left">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" required>
                  First Name
                </Label>
                <Input
                  id="firstName"
                  value={userInfo.first_name ?? ""}
                  onChange={handleChange}
                  placeholder="Enter first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" required>
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  value={userInfo.last_name ?? ""}
                  onChange={handleChange}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={userInfo.email ?? ""}
                readOnly
                className="cursor-not-allowed bg-muted/30"
                title="Email cannot be changed"
              />
              <p className="text-[10px] text-muted-foreground">
                Login email cannot be modified from here.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Access Level</Label>
              <Input
                id="role"
                value={
                  userInfo.user_type === "admin"
                    ? "Super Administrator"
                    : "Administrator"
                }
                disabled
                className="bg-muted/30"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                className="min-w-[140px] bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleSave}
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserProfile;
